import { useQuery, useReactiveVar } from "@apollo/client";
import { compact } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  matchPath,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { GetProfileQueryQueryVariables } from "types/graphql-schema";
import { GetProfileQueryQuery } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import { MeetingTOCDockContext } from "@apps/meeting-new/components/meeting-toc-dock-context";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

import ProfileCareer from "./career-tab/profile-career-tab";
import CoachingPage from "./coaching-tab/profile-coaching-tab";
import ProfileIntroDialog from "./components/profile-intro-dialog";
import getProfileQuery from "./graphql/get-profile-query";
import ImportOneononeDialog from "./oneonone-tab/import-oneonone-dialog";
import { meetingsShowingEmptyCardsVar } from "./oneonone-tab/oneonone-empty-cards-state";
import ProfileOneononeSidebar from "./oneonone-tab/profile-oneonone-sidebar";
import ProfileOneonone from "./oneonone-tab/profile-oneonone-tab";
import useProfileContentTabs from "./overview-tab/components/use-profile-content-tabs";
import ProfileEmptyState from "./profile-empty-state";
import ProfilePageSidebar from "./profile-page-sidebar";
import ProfilePrivateNotes from "./profile-private-notes";
import ProfileResponsiveTabs from "./profile-responsive-tabs";
import ProfileWorkHistory from "./profile-work-history";

const ProfilePage = () => {
  const { path } = useRouteMatch();
  const link = useLink();
  const label = useLabel();
  const loadingDetailsText = "Loading details...";
  const location = useLocation<TFLocationState>();
  const { profileId: profileIdStr } = useParams<{
    profileId: string | undefined;
  }>();
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const profileId = profileIdStr ? parseInt(profileIdStr) : undefined;
  const meetingsShowingEmptyCards = useReactiveVar(
    meetingsShowingEmptyCardsVar,
  );
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  const { data, loading } = useQuery<
    GetProfileQueryQuery,
    GetProfileQueryQueryVariables
  >(getProfileQuery, {
    variables: { userId: profileId || -1 },
    skip: !profileId,
  });
  const queriedProfile = data?.user;
  const profile =
    queriedProfile && queriedProfile.id === profileId
      ? queriedProfile
      : undefined;
  const profileLoading =
    loading || (!!queriedProfile && queriedProfile.id !== profileId);
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  const isAdmin = isAdminVar();
  const canViewCareerTab =
    profile &&
    (isAdmin ||
      profile.isInCurrentUserReportingTree ||
      currentUser.id === profile.id);

  const directReports = assertEdgesNonNull(currentUser.directReports);
  const currentUserIsManagerOfProfile = directReports.find(
    ({ id }) => id === profileId,
  );
  const showPrivateNotes =
    currentUser.id === profile?.id || !!currentUserIsManagerOfProfile;
  const showCoachingTab =
    !!currentUserIsManagerOfProfile &&
    waffle.flag_is_active("coaching-insights");
  const coachingManagerId: number = currentUser.id;

  const showTabs = profileId === currentUser.id || isManager;
  const contentTabs = useProfileContentTabs(profile);
  const availableContentTabs = contentTabs.filter((tab) => tab.available);

  const tabButtons: ToggleButtonGroupType<string>[] = compact([
    {
      value: "oneonone",
      title: label("oneonone", { titleCase: true }),
      active: location.pathname.includes(`/profile/${profileId}/oneonone`),
    },
    showTabs &&
      showPrivateNotes && {
        value: "my-notes",
        title: "Private Notes",
        active: location.pathname.includes(`/profile/${profileId}/my-notes`),
      },
    showTabs &&
      organization.featureFlags.workHistory && {
        value: "work-history",
        title: "Work History",
        active: location.pathname.includes(
          `/profile/${profileId}/work-history`,
        ),
      },
    showTabs &&
      showCoachingTab && {
        value: "coaching",
        title: "Insights",
        active: location.pathname.includes(`/profile/${profileId}/coaching`),
      },
    ...availableContentTabs.map((tab) => ({
      value: tab.value,
      title: tab.label,
      active: location.pathname === `/profile/${profileId}/${tab.value}`,
    })),
    showTabs &&
      canViewCareerTab && {
        value: "career",
        title: "Reviews",
        active: location.pathname.includes(`/profile/${profileId}/career`),
      },
  ]);

  const handleTabClick = (button: ToggleButtonGroupType<string>) => {
    // The 1-on-1 tab lands on /profile/:id/oneonone; ProfileOneonone then
    // replaces the URL with the focused meeting id.
    link.redirect(`/profile/${profileId}/${button.value}`);
  };

  const oneononeMatch = matchPath<{ meetingId?: string }>(location.pathname, {
    path: "/profile/:profileId/oneonone/:meetingId?",
  });
  const isOneononeTab = !!oneononeMatch;
  const isProfileOneononeRoute =
    isOneononeTab ||
    !!matchPath(location.pathname, {
      path,
      exact: true,
    });
  const canRenderProfileOneononeRoute =
    isProfileOneononeRoute &&
    profileId !== undefined &&
    (profileLoading || !!profile);
  const selectedMeetingId = oneononeMatch?.params.meetingId
    ? parseInt(oneononeMatch.params.meetingId)
    : null;

  const [useRollingWorkHistoryDateRange, setUseRollingWorkHistoryDateRange] =
    useState(false);

  const sidebarPreferenceKey = "profileSidebarExpanded" as const;

  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const introDialogSeen = uiPreferenceCache.profileIntroDialogSeen;
  const hasMountedRef = useRef(false);
  // The DOM node the floating 1-on-1 TOC portals into. Owned here so its
  // position is decided alongside the sidebar in ONE place (see
  // MeetingTOCDockContext). Only the 1-on-1 tab portals into it; on other
  // tabs the dock stays empty.
  const [tocDockEl, setTocDockEl] = useState<HTMLDivElement | null>(null);
  const sidebarVisible = !!uiPreferenceCache.profileSidebarExpanded;
  const [settledProfileOneononeId, setSettledProfileOneononeId] = useState<
    number | null
  >(null);
  const profileOneononeSettled = settledProfileOneononeId === profileId;
  const handleProfileOneononeSettledChange = useCallback(
    (isSettled: boolean) => {
      setSettledProfileOneononeId(
        isSettled && profileId !== undefined ? profileId : null,
      );
    },
    [profileId],
  );

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    setUseRollingWorkHistoryDateRange(false);
  }, [profileId, isOneononeTab]);

  // The intro dialog auto-shows once per user on their first profile visit and
  // is then remembered via the UI preference cache. `introManuallyOpened`
  // tracks the header info button so the dialog can be re-opened on demand
  // after it has been seen.
  const [showIntroDialog, setShowIntroDialog] = useState(false);
  const [introManuallyOpened, setIntroManuallyOpened] = useState(false);
  // While impersonating, never auto-show the intro or persist its "seen" flag:
  // the preference belongs to the impersonated user, so marking it seen here
  // would rob them of their own first-visit intro.
  const isImpersonating = window.TF.hijacked;

  useEffect(() => {
    // Never override a manual open triggered by the header info button.
    if (introManuallyOpened) return;
    // Auto-show for a loaded profile the user hasn't seen the intro for, and
    // auto-close if the value resolves to seen — covers the cache-and-network
    // getLoggedInUser refresh on reload briefly serving a stale "not seen".
    setShowIntroDialog(!!profile && !introDialogSeen && !isImpersonating);
  }, [profile, introDialogSeen, introManuallyOpened, isImpersonating]);

  const handleOpenIntroDialog = () => {
    setIntroManuallyOpened(true);
    setShowIntroDialog(true);
  };

  const handleCloseIntroDialog = () => {
    setIntroManuallyOpened(false);
    setShowIntroDialog(false);
    if (!introDialogSeen && !isImpersonating) {
      saveUiPreference({ profileIntroDialogSeen: true });
    }
  };

  return (
    <Layout>
      <MeetingTOCDockContext.Provider
        value={{ dockEl: tocDockEl, sidebarVisible }}
      >
        <Layout.Header
          className="bg-[#FCFCFD]"
          rightSideClassName="!gap-1.5"
          sidebarExpandedUiPreferenceKey={sidebarPreferenceKey}
          title={
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {profile ? (
                <Button
                  size={ButtonSize.medium}
                  theme={buttonTheme.text}
                  to={getUrlUsingTypename(profile)}
                >
                  <Avatar user={profile} size="4" className="mr-1" />
                  {profile.name}
                </Button>
              ) : (
                <span>Profile</span>
              )}
              {tabButtons.length > 0 && (
                <ProfileResponsiveTabs
                  buttons={tabButtons}
                  onClick={handleTabClick}
                />
              )}
            </div>
          }
        >
          {profile && (
            <Dropdown
              aria-label="Profile actions"
              size={ButtonSize.medium}
              align="end"
              options={compact([
                // "Import 1-on-1s" only on a direct report's profile; the
                // "About coaching space" item shows on every profile.
                currentUserIsManagerOfProfile && {
                  label: `Import ${label("oneonone", { capitalize: true, pluralize: true })}`,
                  onClick: () => setIsImportDialogOpen(true),
                },
                {
                  label: "About the coaching space",
                  onClick: handleOpenIntroDialog,
                },
              ])}
            />
          )}
        </Layout.Header>
        {profile && isImportDialogOpen && (
          <ImportOneononeDialog
            profileId={profile.id}
            onClose={() => setIsImportDialogOpen(false)}
          />
        )}
        <Layout.Container>
          <Layout.Main
            // Widen the column for the get-started cards (1-on-1 tab with no
            // meeting focused) and the in-meeting empty cards; a focused
            // meeting uses the narrower reading width.
            width={
              isOneononeTab &&
              (selectedMeetingId === null || meetingsShowingEmptyCards.size > 0)
                ? "xl"
                : "md"
            }
            // Match the meeting-page background across every profile tab so
            // the sticky meeting header (bg-[#FCFCFD]) on the 1-on-1 tab
            // blends with the surrounding page, and the rest of the profile
            // shares one consistent surface color.
            className="bg-[#FCFCFD]"
            sidebarExpandedUiPreferenceKey={sidebarPreferenceKey}
          >
            {profileLoading && !canRenderProfileOneononeRoute ? (
              <Loading>Loading profile...</Loading>
            ) : !profile && !canRenderProfileOneononeRoute ? (
              <div>
                <ProfileEmptyState>Profile not found.</ProfileEmptyState>
              </div>
            ) : (
              <Switch>
                <Route exact path={path}>
                  {profileId !== undefined ? (
                    <Redirect to={`/profile/${profileId}/oneonone`} />
                  ) : (
                    <ProfileEmptyState>Profile not found.</ProfileEmptyState>
                  )}
                </Route>
                <Route path={`${path}/oneonone/:meetingId?`}>
                  {profileId !== undefined ? (
                    <ProfileOneonone
                      key={profileId}
                      profileId={profileId}
                      profileFirstName={profile?.firstName}
                      profileEmail={profile?.email}
                      onSettledChange={handleProfileOneononeSettledChange}
                      onUseRollingWorkHistoryDateRangeChange={
                        setUseRollingWorkHistoryDateRange
                      }
                    />
                  ) : (
                    <ProfileEmptyState>Profile not found.</ProfileEmptyState>
                  )}
                </Route>
                {profile && showPrivateNotes && (
                  <Route exact path={`${path}/my-notes`}>
                    <ProfilePrivateNotes
                      profileId={profile.id}
                      disabled={false}
                      showHeader
                    />
                  </Route>
                )}
                {availableContentTabs.map((tab) => (
                  <Route exact key={tab.value} path={`${path}/${tab.value}`}>
                    {tab.render()}
                  </Route>
                ))}
                {profile && (
                  <Route exact path={`${path}/work-history`}>
                    <ProfileWorkHistory profileId={profile.id} />
                  </Route>
                )}
                {profile && canViewCareerTab && (
                  <Route exact path={`${path}/career`}>
                    <ProfileCareer profileId={profile.id} />
                  </Route>
                )}
                {profile && showCoachingTab && (
                  <Route exact path={`${path}/coaching`}>
                    <CoachingPage
                      profileId={profile.id}
                      managerId={coachingManagerId}
                    />
                  </Route>
                )}
              </Switch>
            )}
          </Layout.Main>
          {!profile || (isOneononeTab && !profileOneononeSettled) ? (
            <Layout.Sidebar sidebarExpandedUiPreferenceKey="profileSidebarExpanded">
              {isProfileOneononeRoute ? (
                <Loading className="mt-6">{loadingDetailsText}</Loading>
              ) : (
                <div />
              )}
            </Layout.Sidebar>
          ) : isOneononeTab && selectedMeetingId !== null ? (
            // A 1-on-1 meeting is focused: show its sidebar (My Notes / Ask AI).
            <ProfileOneononeSidebar
              key={profile.id}
              meetingId={selectedMeetingId}
              profileId={profile.id}
              loadingText={loadingDetailsText}
              useRollingWorkHistoryDateRange={useRollingWorkHistoryDateRange}
            />
          ) : (
            // Any other tab, or the 1-on-1 tab with no meeting yet (empty
            // profile): show the profile details sidebar instead of nothing.
            <ProfilePageSidebar profileId={profile.id} />
          )}
          {/* TOC dock — anchored to the sidebar's left edge when the sidebar
            is visible, otherwise pinned 16px from the viewport's right edge.
            One source of truth for TOC positioning instead of two coupled
            offsets (sidebar + TOC) that had to be kept in sync across
            breakpoints. Widths match `widthBreakpointClassName` in Layout:
            w-96 (24rem), xl=w-120 (30rem), 2xl=w-144 (36rem). Only the 1-on-1
            tab portals into this dock; on other tabs it stays empty. */}
          {isOneononeTab && (
            <div
              ref={setTocDockEl}
              className={classNames(
                // The floating TOC needs room between Main and the sidebar.
                // When the sidebar is expanded the gap doesn't open up until
                // lg+; with the sidebar collapsed, md+ is enough. Below those
                // breakpoints the inline chevrons + calendar dropdown in the
                // meeting header take over (see
                // previous-meetings-popover-panel.tsx).
                sidebarVisible ? "hidden lg:block" : "hidden md:block",
                // Anchored 16px below Layout.Header (which is 56px tall).
                "fixed top-[72px] z-30",
                sidebarVisible
                  ? "right-[calc(24rem+1rem)] xl:right-[calc(30rem+1rem)] 2xl:right-[calc(36rem+1rem)]"
                  : "right-4",
              )}
            />
          )}
        </Layout.Container>
        {showIntroDialog && (
          <ProfileIntroDialog onClose={handleCloseIntroDialog} />
        )}
      </MeetingTOCDockContext.Provider>
    </Layout>
  );
};

export default ProfilePage;
